import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { CheckCircle, Clock, Eye, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { calcDatePreset } from '../../components/fields/DateRangePicker';
import DateRangeSelect from '../../components/fields/DateRangeSelect';
import PaginationComponent from '../../components/PaginationComponent';
import { employeeAPI, qcResultAPI, qcSessionAPI } from '../../services';
import QcResultDetailModal from './QcResultDetailModal';

export const SOURCE_KIND_LABELS = {
  SHIFT_TASK: 'Task ca làm',
  GAME_CHECK: 'Kiểm tra Game',
  LOYALTY_REVIEW: 'Duyệt Loyalty',
  TABLE_PREP: 'Chuẩn bị bàn',
};
export const SOURCE_KIND_VARIANTS = {
  SHIFT_TASK: 'info',
  GAME_CHECK: 'primary',
  LOYALTY_REVIEW: 'warning',
  TABLE_PREP: 'dark',
};
export const QC_STATUS_LABELS = { PENDING: 'Chờ review', IN_REVIEW: 'Đang review', PASSED: 'Đạt', FAILED: 'Không đạt' };
export const QC_STATUS_VARIANTS = { PENDING: 'secondary', IN_REVIEW: 'warning', PASSED: 'success', FAILED: 'danger' };
export const RESOLUTION_LABELS = {
  PENALIZED: 'Đã trừ điểm',
  WARNED: 'Đã nhắc nhở',
  COACHED: 'Đã hướng dẫn lại',
  NO_ACTION: 'Không cần hành động',
  DISPUTED: 'Đang khiếu nại',
};

const PAGE_SIZE = 20;
/** Số phiên nạp cho dropdown. Phiên giờ sinh theo yêu cầu nên nhiều hơn hẳn bản cũ. */
const SESSION_OPTIONS_LIMIT = 200;

/**
 * Nhãn nhận diện 1 phiên. Chỉ dùng `planned_task_date` là không đủ: nhiều nhân viên
 * cùng nhận nhiệm vụ QC trong một ngày sẽ cho ra loạt lựa chọn giống hệt nhau.
 */
export const formatSessionLabel = (s) => {
  if (!s) return '';
  const who = s.qc_employee?.full_name;
  let claimed = '';
  try {
    if (s.claimed_at) claimed = format(new Date(s.claimed_at), ' HH:mm');
  } catch {
    claimed = '';
  }
  return [s.planned_task_date + claimed, s.branch?.name, who].filter(Boolean).join(' — ');
};

const formatDateTime = (v) => {
  if (!v) return '-';
  try {
    return format(new Date(v), 'dd/MM/yyyy HH:mm');
  } catch {
    return '-';
  }
};

export default function QcResultsListTab({ sharedFilters, onSharedFiltersChange }) {
  const navigate = useNavigate();
  const [range, setRange] = useState(() => calcDatePreset('last3months'));
  const [sourceKind, setSourceKind] = useState('');
  const [qcStatus, setQcStatus] = useState('FAILED');
  const [handledOnly, setHandledOnly] = useState('false');
  const [employees, setEmployees] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ passed: 0, failed: 0, pending: 0, in_review: 0, handled: 0, unhandled_failed: 0 });
  const [meta, setMeta] = useState({ total: 0, total_pages: 0 });
  const [detailTarget, setDetailTarget] = useState(null);

  const branchId = sharedFilters.branch_id;

  useEffect(() => {
    employeeAPI
      .getEmployees({ page: 1, limit: 100, status: 'ACTIVE' })
      .then((res) => {
        const list = res?.data || res?.items || res || [];
        setEmployees(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    qcSessionAPI
      .getQcSessions({ branch_id: branchId || undefined, limit: SESSION_OPTIONS_LIMIT })
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setSessions(list);
      })
      .catch(() => {});
  }, [branchId]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        branch_id: branchId || undefined,
        employee_id: sharedFilters.employee_id || undefined,
        session_id: sharedFilters.session_id || undefined,
        source_kind: sourceKind || undefined,
        qc_status: qcStatus || undefined,
        handled: handledOnly || undefined,
        // Lọc theo 1 phiên đã đủ hẹp — giữ thêm khoảng ngày mặc định "tháng này" sẽ
        // giấu mất kết quả của phiên cũ (drill-through từ tab Theo phiên trông như hỏng)
        start_date: sharedFilters.session_id ? undefined : range.from,
        end_date: sharedFilters.session_id ? undefined : range.to,
        page,
        limit: PAGE_SIZE,
      };
      const res = await qcResultAPI.getQcResults(params);
      setRows(Array.isArray(res?.data) ? res.data : []);
      setStats(res?.stats || { passed: 0, failed: 0, pending: 0, in_review: 0, handled: 0, unhandled_failed: 0 });
      setMeta(res?.meta || { total: 0, total_pages: 0 });
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Không tải được kết quả QC');
    } finally {
      setLoading(false);
    }
  }, [branchId, sharedFilters.employee_id, sharedFilters.session_id, sourceKind, qcStatus, handledOnly, range, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, sharedFilters.employee_id, sharedFilters.session_id, sourceKind, qcStatus, handledOnly, range]);

  const filteredEmployeeName = employees.find((e) => e.id === sharedFilters.employee_id)?.full_name;
  const filteredSession = sessions.find((s) => s.id === sharedFilters.session_id);

  return (
    <>
      <Row className="mb-3">
        {[
          { label: 'Đạt', value: stats.passed || 0, color: 'success', Icon: CheckCircle },
          { label: 'Không đạt', value: stats.failed || 0, color: 'danger', Icon: XCircle },
          { label: 'Đang review', value: stats.in_review || 0, color: 'warning', Icon: Clock },
          { label: 'Chờ review', value: stats.pending || 0, color: 'secondary', Icon: Clock },
          { label: 'Chưa xử lý (trong lỗi)', value: stats.unhandled_failed || 0, color: 'danger', Icon: XCircle },
        ].map(({ label, value, color, Icon }) => (
          <Col key={label} md={2} className="mb-2">
            <Card className={`h-100 border-${color}`}>
              <Card.Body className="text-center py-2">
                <Icon size={20} className={`text-${color} mb-1`} />
                <h5 className={`text-${color} mb-0`}>{value}</h5>
                <small className="text-muted">{label}</small>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3">
            {/* #175019 — bỏ ô chọn chi nhánh riêng: chi nhánh chỉ chọn ở thanh trên đầu. */}
            <Col md={3}>
              <Form.Label>Nguồn QC</Form.Label>
              <Form.Select value={sourceKind} onChange={(e) => setSourceKind(e.target.value)}>
                <option value="">Tất cả nguồn</option>
                {Object.entries(SOURCE_KIND_LABELS).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Trạng thái QC</Form.Label>
              <Form.Select value={qcStatus} onChange={(e) => setQcStatus(e.target.value)}>
                <option value="">Tất cả trạng thái</option>
                {Object.entries(QC_STATUS_LABELS).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Xử lý</Form.Label>
              <Form.Select value={handledOnly} onChange={(e) => setHandledOnly(e.target.value)}>
                <option value="">Tất cả</option>
                <option value="false">Chỉ chưa xử lý</option>
                <option value="true">Chỉ đã xử lý</option>
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label>Nhân viên</Form.Label>
              <Form.Select
                value={sharedFilters.employee_id}
                onChange={(e) => onSharedFiltersChange((prev) => ({ ...prev, employee_id: e.target.value }))}
              >
                <option value="">Tất cả nhân viên</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.full_name || emp.username}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Phiên QC</Form.Label>
              <Form.Select
                value={sharedFilters.session_id}
                onChange={(e) => onSharedFiltersChange((prev) => ({ ...prev, session_id: e.target.value }))}
              >
                <option value="">Tất cả phiên</option>
                {/* Nhiều NV nhận QC cùng ngày -> nhãn phải kèm QC viên, không thì các dòng trùng hệt nhau */}
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {formatSessionLabel(s)}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <DateRangeSelect fromDate={range.from} toDate={range.to} onChange={(from, to) => setRange({ from, to })} defaultPreset="last3months" />
            </Col>
          </Row>

          {(filteredEmployeeName || filteredSession) && (
            <div className="mt-2 d-flex gap-2 flex-wrap">
              {filteredEmployeeName && (
                <Badge bg="info" className="d-flex align-items-center gap-1">
                  Nhân viên: {filteredEmployeeName}
                  <span
                    role="button"
                    className="ms-1"
                    onClick={() => onSharedFiltersChange((prev) => ({ ...prev, employee_id: '' }))}
                  >
                    ×
                  </span>
                </Badge>
              )}
              {filteredSession && (
                <Badge bg="info" className="d-flex align-items-center gap-1">
                  Phiên: {formatSessionLabel(filteredSession)}
                  <span
                    role="button"
                    className="ms-1"
                    onClick={() => onSharedFiltersChange((prev) => ({ ...prev, session_id: '' }))}
                  >
                    ×
                  </span>
                </Badge>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-4 text-muted">Không có kết quả QC nào theo bộ lọc hiện tại.</div>
      ) : (
        <>
          <div className="table-responsive">
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Ngày QC</th>
                  <th>Phiên</th>
                  <th>Nguồn</th>
                  <th>Nội dung</th>
                  <th>Nhân viên</th>
                  <th>Người QC</th>
                  <th>Kết quả</th>
                  <th>Xử lý</th>
                  <th>Điểm</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.source_kind}:${row.source_id}`}>
                    <td>{formatDateTime(row.qc_reviewed_at || row.occurred_at)}</td>
                    <td>
                      {row.qc_session ? (
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0"
                          onClick={() => navigate(`/qc-sessions/${row.qc_session.id}`)}
                        >
                          {row.qc_session.planned_task_date}
                          {row.qc_session.qc_employee_name ? ` · ${row.qc_session.qc_employee_name}` : ''}
                        </Button>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td><Badge bg={SOURCE_KIND_VARIANTS[row.source_kind] || 'secondary'}>{SOURCE_KIND_LABELS[row.source_kind] || row.source_kind}</Badge></td>
                    <td>
                      <div className="fw-semibold">{row.title}</div>
                      {row.subtitle && <small className="text-muted">{row.subtitle}</small>}
                    </td>
                    <td>{row.worker?.full_name || <span className="text-muted">Không xác định</span>}</td>
                    <td>{row.qc_reviewer?.full_name || '-'}</td>
                    <td>
                      <Badge bg={QC_STATUS_VARIANTS[row.qc_status] || 'secondary'}>
                        {QC_STATUS_LABELS[row.qc_status] || row.qc_status || '-'}
                      </Badge>
                    </td>
                    <td>
                      {row.handling?.is_handled ? (
                        <Badge bg="success">{RESOLUTION_LABELS[row.handling.resolution] || 'Đã xử lý'}</Badge>
                      ) : (
                        <Badge bg="light" text="dark">Chưa xử lý</Badge>
                      )}
                    </td>
                    <td>
                      {row.handling?.point ? (
                        <Badge bg={row.handling.point.type === 'ADD' ? 'success' : 'danger'}>
                          {row.handling.point.type === 'ADD' ? '+' : '-'}
                          {row.handling.point.value}
                        </Badge>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => setDetailTarget({ sourceKind: row.source_kind, id: row.source_id })}
                      >
                        <Eye size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <PaginationComponent
            totalItems={meta.total || 0}
            itemsPerPage={PAGE_SIZE}
            currentPage={page}
            onPageChange={setPage}
            loading={loading}
          />
        </>
      )}

      {detailTarget && (
        <QcResultDetailModal
          sourceKind={detailTarget.sourceKind}
          id={detailTarget.id}
          onHide={() => setDetailTarget(null)}
          onChanged={fetchData}
        />
      )}
    </>
  );
}
